import {
  Product,
  ProductVariant,
  ProductVariantAttribute,
  SortOrder,
} from '@/__generated__/graphql';
import { useMutation, useQuery } from '@apollo/client';
import {
  ActionIcon,
  Button,
  Checkbox,
  ColorSwatch,
  Divider,
  Fieldset,
  Grid,
  Group,
  Loader,
  NumberInput,
  Paper,
  Select,
  Stack,
  TagsInput,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconArrowUp,
  IconDeviceFloppy,
  IconPlus,
  IconProgress,
  IconTrash,
  IconWand,
} from '@tabler/icons-react';
import { useMemo, useRef, useState } from 'react';
import { productModalDocument } from '../graphql/get-product';
import { productLivesDocument, productsCodeDocument } from '../graphql/get-products';
import { upsertProductDocument } from '../graphql/upsert-product';
import { colorMap } from '@/utils/colorMap';

type FormProps = Partial<Pick<Product, 'id'>> &
  Pick<
    Product,
    | 'code'
    | 'name'
    | 'description'
    | 'price'
    | 'active'
    | 'location'
    | 'image'
    | 'canRemove'
    | 'liveId'
  > & {
    variants: (Partial<Pick<ProductVariant, 'id'>> &
      Pick<ProductVariant, 'stock'> & {
        attributes?: (Partial<Pick<ProductVariantAttribute, 'id'>> &
          Pick<ProductVariantAttribute, 'name' | 'value'>)[];
      })[];
  };

const normalizeAttrName = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

const SIZE_ATTR_NAME = 'velkost';

const buildSizeDescription = (variants: FormProps['variants']): string => {
  const seen = new Set<string>();
  const sizes: string[] = [];

  for (const variant of variants) {
    for (const attr of variant.attributes ?? []) {
      if (normalizeAttrName(attr.name) !== SIZE_ATTR_NAME) continue;
      const v = attr.value.trim();
      if (!v || seen.has(v)) continue;
      seen.add(v);
      sizes.push(v);
    }
  }

  return sizes.map((v) => `${v}()`).join(' ');
};

export const ProductUpsertModal = ({
  productId,
  liveId,
  defaultValues,
}: {
  productId?: string;
  liveId?: string;
  defaultValues?: Partial<FormProps>;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [attributes, setAttributes] = useState<string[]>([]);
  const [attributeValues, setAttributeValues] = useState<Record<string, string[]>>({});
  const [defaultStock, setDefaultStock] = useState<number | string>(0);

  const { data: lives, loading: getLivesLoading } = useQuery(productLivesDocument, {
    variables: { orderBy: { date: SortOrder.Desc } },
  });

  const { data, loading } = useQuery(productModalDocument, {
    variables: {
      where: { id: productId },
    },
    skip: !productId,
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ product }) => {
      if (!product) return;

      form.setValues({
        id: product.id,
        code: product.code,
        name: product.name,
        description: product.description,
        active: product.active,
        image: product.image,
        canRemove: product.canRemove,
        location: product.location,
        price: product.price,
        liveId: product.liveId,
        variants: product.variants?.map(({ id, stock, attributes }) => ({
          id,
          stock,
          attributes: attributes?.map(({ id, name, value }) => ({ id, name, value })),
        })) ?? [{ stock: 0 }],
      });

      setAttributes(
        Array.from(
          new Set(
            product.variants?.flatMap(
              (variant) => variant.attributes?.map((attr) => attr.name) ?? []
            )
          )
        )
      );
    },
    fetchPolicy: 'network-only',
  });

  const [upsertProduct, { loading: upserting }] = useMutation(upsertProductDocument, {
    refetchQueries: 'active',
    onCompleted: (data) =>
      notifications.show({ title: 'Product saved', message: data.upsertProduct.id }),
    onError: (error) =>
      notifications.show({ title: 'Failed to save product', message: error.message }),
  });

  const {
    data: products,
    loading: loadingAllProducts,
    refetch: refetchProducts,
  } = useQuery(productsCodeDocument, {
    variables: { where: { liveId: { equals: liveId || data?.product?.liveId } } },
    skip: !liveId && !data?.product?.liveId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ products }) => {
      if (products.length === 0 || !defaultValues?.active) return;

      const sortedProducts = products
        .slice()
        .filter((product) => product.active && /^[0-9]+$/.test(product.code)) // Keep only numeric codes
        .map((product) => parseInt(product.code, 10)) // Convert to numbers
        .sort((a, b) => b - a);

      if (sortedProducts.length === 0) return;

      const nextCode = (sortedProducts[0] + 1).toString().padStart(2, '0');

      form.setFieldValue('code', nextCode);
    },
  });

  const form = useForm<FormProps>({
    initialValues: {
      code: defaultValues?.code ?? '',
      name: defaultValues?.name ?? '',
      price: defaultValues?.price ?? 0,
      description: defaultValues?.description,
      active: defaultValues?.active ?? false,
      canRemove: defaultValues?.canRemove ?? false,
      liveId: liveId ?? '',
      variants: defaultValues?.variants ?? [{ stock: 0 }],
    },
    validate: {
      code: (value) =>
        data?.product?.code !== value && products?.products.some((p) => p.code === value)
          ? `Kód ${value} už existuje!`
          : isNotEmpty('Zadaj kód!')(value),
      name: isNotEmpty('Zadaj názov!'),
      price: (value, values) =>
        values.active && value === 0 && !values.name.includes('ZADARMO')
          ? 'Cena musí byť nastavená pred spustením!'
          : isNotEmpty('Zadaj cenu!')(value),
      liveId: (value) =>
        value !== data?.product?.liveId &&
        (data?.product?.variants?.some((v) => v._count.lines > 0) || data?.product?.active)
          ? `Tento produkt už bol prezentovaný v predošlej LIVE!`
          : isNotEmpty('Zadaj LIVE!')(value),
      variants: {
        stock: (value) => (value < 0 ? 'Sklad nemôže byť záporný!' : null),
        attributes: (value, values, path) => {
          if (value?.some((attr) => attr.value === '')) return 'Prázdna hodnota!';
          if (value?.some((attr) => /\s/g.test(attr.value))) return 'Medzera v hodnote!';

          const match = path.match(/variants\.(\d+)\.attributes/);
          if (!match) return null; // Skip validation if the path doesn't match

          if (values.variants?.length === 1 && (value?.length ?? 0) > 0)
            return `Atribúty nie sú povolené, ak existuje len jeden variant.`;

          const [, variantIndex] = match.map(Number);

          for (const [vIndex, variant] of values.variants.entries()) {
            if (vIndex === variantIndex) continue;

            // TODO: Fix duplicate check -> test this
            // value.every((attr, index) => attr.value === (variant.attributes?.[index]?.value ?? null))

            if (
              variant.attributes?.every(
                (attr, attrIndex) =>
                  attr.name === value?.[attrIndex].name && attr.value === value?.[attrIndex].value
              )
            )
              return `Duplicita! ${variantIndex + 1} === ${vIndex + 1}`;
          }

          return null; // No validation errors
        },
      },
    },
  });

  const addVariant = () => {
    form.setFieldValue('variants', [
      {
        stock: 0,
        attributes:
          attributes.length > 0 ? attributes.map((name) => ({ name, value: '' })) : undefined,
      },
      ...form.getValues().variants,
    ]);

    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const updateAttributes = (newAttributes: string[]) => {
    setAttributes(newAttributes);

    if (newAttributes.length === 0) {
      form.setFieldValue('variants', [{ stock: 0 }]);
    } else {
      form.setFieldValue(
        'variants',
        form.getValues().variants.map((variant, variantIndex) => ({
          ...variant,
          attributes: newAttributes.map((name) => {
            const previousAttribute = data?.product?.variants?.[variantIndex]?.attributes?.find(
              (attr) => attr.name === name
            );

            const existing = variant.attributes?.find((attr) => attr.name === name);

            return {
              id: existing?.id ?? previousAttribute?.id, // Preserve ID if it exists
              name,
              value: existing?.value ?? previousAttribute?.value ?? '', // Preserve value from form or query data
            };
          }),
        }))
      );
    }
  };

  const handleSubmit = async ({
    id: productId,
    liveId,
    code,
    name,
    description,
    image,
    location,
    price,
    active,
    canRemove,
    variants,
  }: typeof form.values) => {
    const originalVariants = data?.product?.variants ?? [];
    const currentVariants = form.getValues().variants;

    // Deleted variants: Present in query but missing in form values
    const deletedVariants = originalVariants
      .filter((orig) => !currentVariants.some((curr) => curr.id === orig.id))
      .map((variant) => variant.id);

    // Deleted attributes: Present in query but missing in current attributes
    const deletedAttributes = originalVariants.flatMap(
      (orig) =>
        orig.attributes
          ?.filter(
            (origAttr) =>
              !currentVariants
                .find((curr) => curr.id === orig.id)
                ?.attributes?.some((currAttr) => currAttr.id === origAttr.id)
          )
          .map((attr) => attr.id) ?? [] // Fallback to empty array if `attributes` is undefined
    );

    const wasActive = data?.product?.active;

    // Perform the mutation
    const newProduct = await upsertProduct({
      variables: {
        where: { id: productId ?? '' },
        create: {
          live: { connect: { id: liveId } },
          code,
          name,
          description,
          image,
          location,
          price,
          active,
          canRemove,
          variants: {
            create: variants.map(({ stock, attributes }) => ({
              stock,
              attributes: {
                create: attributes?.map(({ name, value }) => ({ name, value })),
              },
            })),
          },
        },
        update: {
          code: { set: code },
          name: { set: name },
          ...(description ? { description: { set: description } } : {}),
          ...(image ? { image: { set: image } } : {}),
          ...(location ? { location: { set: location } } : {}),
          price: { set: price },
          active: { set: active },
          canRemove: { set: canRemove },
          live: { connect: { id: liveId } },
          variants: {
            upsert: variants.map(({ id: variantId, stock, attributes }) => ({
              where: { id: variantId ?? '' },
              create: {
                stock,
                attributes: {
                  create: attributes?.map(({ name, value }) => ({ name, value })),
                },
              },
              update: {
                stock: { set: stock },
                attributes: {
                  upsert: attributes?.map(({ id: attributeId, name, value }) => ({
                    where: attributeId
                      ? { id: attributeId }
                      : variantId
                        ? { variantId_name: { variantId, name } }
                        : { id: '' },
                    create: { name, value },
                    update: { value: { set: value } },
                  })),
                  deleteMany: deletedAttributes.map((attributeId) => ({
                    id: { equals: attributeId },
                  })),
                },
              },
            })),
            // Delete the variants
            deleteMany: deletedVariants.map((variantId) => ({
              id: { equals: variantId },
            })),
          },
        },
      },
    });

    if (!newProduct.data?.upsertProduct.id) return;

    modals.closeAll();

    if (!productId || wasActive !== active)
      window
        .open(
          `https://api.laskykrasky.sk/api/live/product?productId=${newProduct.data?.upsertProduct.id}`,
          '_blank'
        )
        ?.focus();
  };

  const generatedSizeDescription = useMemo(
    () => buildSizeDescription(form.values.variants),
    [form.values.variants]
  );

  const sizeDescriptionDisabled =
    generatedSizeDescription.length === 0 || (form.values.description ?? '').length > 0;

  if (loading && productId) {
    return (
      <Stack align="center" justify="center" p="md">
        <Loader />
      </Stack>
    );
  }

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();

        await refetchProducts();

        form.onSubmit(handleSubmit)(e);
      }}
    >
      <Stack>
        <Paper component={Stack} p="md" withBorder>
          <Group justify="space-between">
            <Group>
              <Text fw={500}>Produkt ({productId})</Text>
            </Group>
            <Checkbox
              {...form.getInputProps('canRemove', { type: 'checkbox' })}
              label="Povoliť rušenie?"
            />
            <Checkbox {...form.getInputProps('active', { type: 'checkbox' })} label="Zapnutý?" />
            <Button
              size="sm"
              type="submit"
              leftSection={<IconDeviceFloppy size={16} />}
              loading={upserting || loadingAllProducts}
            >
              Uložiť
            </Button>
          </Group>
          <Divider />
          <Select
            {...form.getInputProps('liveId')}
            label="LIVE"
            disabled={getLivesLoading}
            rightSection={getLivesLoading && <Loader size={16} />}
            data={lives?.lives.map(({ id, name }) => ({ value: id, label: name }))}
          />
          {/* <Group justify="space-between">
            <Text c="teal">
              {products?.products
                .slice()
                .filter((product) => product.active && /^[0-9]+$/.test(product.code)) // Keep only numeric codes
                .map((product) => parseInt(product.code, 10)) // Convert to numbers
                .sort((a, b) => b - a) // Sort ascending
                .slice(0, 5)
                .join(', ')}
            </Text>
            <Text c="orange">
              {products?.products
                .slice()
                .filter(
                  (product) => !product.active && product.price > 0 && /^[0-9]+$/.test(product.code)
                ) // Keep only numeric codes
                .map((product) => parseInt(product.code, 10)) // Convert to numbers
                .sort((a, b) => b - a) // Sort ascending
                .slice(0, 5)
                .join(', ')}
            </Text>
          </Group> */}
          <Group grow>
            <TextInput
              {...(!defaultValues?.active ? { 'data-autofocus': true } : {})}
              {...form.getInputProps('code')}
              label="Kód"
              placeholder="P0123..."
              disabled={defaultValues?.active && loadingAllProducts}
              rightSection={
                <ActionIcon
                  variant="subtle"
                  onClick={async () => {
                    const productsQuery = await refetchProducts();

                    const sortedProducts = productsQuery.data.products
                      .slice()
                      .filter((product) => product.active && /^[0-9]+$/.test(product.code)) // Keep only numeric codes
                      .map((product) => parseInt(product.code, 10)) // Convert to numbers
                      .sort((a, b) => b - a);

                    if (sortedProducts.length === 0) return;

                    const nextCode = (sortedProducts[0] + 1).toString().padStart(2, '0');

                    form.setFieldValue('code', nextCode);
                    form.setFieldValue('active', true);
                  }}
                >
                  <IconArrowUp size={16} />
                </ActionIcon>
              }
            />
            <TextInput
              {...(defaultValues?.active ? { 'data-autofocus': true } : {})}
              {...form.getInputProps('name')}
              label="Názov"
              placeholder="Tričko (P0123)..."
              onBlur={() => {
                if (
                  defaultValues?.active ||
                  productId ||
                  form.getValues().name.includes(form.getValues().code)
                )
                  return;

                form.setFieldValue('name', `${form.getValues().name} (${form.getValues().code})`);
              }}
            />
            <NumberInput
              {...form.getInputProps('price')}
              label="Cena"
              suffix=" €"
              placeholder="0..."
            />
          </Group>
          <TextInput
            {...form.getInputProps('description')}
            label="Popis"
            placeholder="1(S/M) 2(L/XL)..."
            rightSection={
              <Tooltip
                label={
                  sizeDescriptionDisabled
                    ? (form.values.description ?? '').length > 0
                      ? 'Najprv vymažte existujúci popis'
                      : 'Žiadny atribút Veľkosť'
                    : 'Generovať popis z veľkostí variantov'
                }
                events={{ hover: true, focus: true, touch: true }}
              >
                <ActionIcon
                  variant="subtle"
                  disabled={sizeDescriptionDisabled}
                  onClick={() => form.setFieldValue('description', generatedSizeDescription)}
                >
                  <IconWand size={16} />
                </ActionIcon>
              </Tooltip>
            }
          />
        </Paper>

        {/* Attributes Management */}
        <Paper component={Stack} p="md" withBorder>
          <Group justify="space-between">
            <Text fw={500}>Varianty</Text>
            <ActionIcon variant="filled" onClick={addVariant} disabled={attributes.length === 0}>
              <IconPlus size={16} />
            </ActionIcon>
          </Group>
          {attributes.length > 0 &&
            !data?.product &&
            (form.values.variants.length <= 1 ||
              form.values.variants.every((v) =>
                (v.attributes ?? []).every((a) => a.value === '')
              )) && (
              <Group justify="space-between" align="center">
                {attributes.map((attr) => (
                  <TagsInput
                    key={attr}
                    label={`Hodnoty pre ${attr}`}
                    value={attributeValues[attr] ?? []}
                    onChange={(values) =>
                      setAttributeValues({ ...attributeValues, [attr]: values })
                    }
                    style={{ flex: 1 }}
                    splitChars={[',', ' ', '|']}
                  />
                ))}
                <NumberInput
                  label="Sklad"
                  value={defaultStock}
                  onChange={setDefaultStock}
                  min={0}
                  allowDecimal={false}
                  allowNegative={false}
                  allowLeadingZeros={false}
                  inputContainer={(children) => (
                    <Group align="center" wrap="nowrap">
                      {children}
                      <ActionIcon
                        variant="filled"
                        onClick={() => {
                          const keys = Object.keys(attributeValues);
                          const sets = keys.map((k) => attributeValues[k]);

                          if (keys.length === 0 || sets.some((s) => s.length === 0)) return;

                          const combos = sets.reduce<string[][]>(
                            (a, b) => a.flatMap((x) => b.map((y) => [...x, y])),
                            [[]]
                          );

                          form.setFieldValue(
                            'variants',
                            combos.map((combo) => ({
                              stock: +defaultStock,
                              attributes: combo.map((value, index) => ({
                                name: keys[index],
                                value,
                              })),
                            }))
                          );
                        }}
                      >
                        <IconProgress size={16} />
                      </ActionIcon>
                    </Group>
                  )}
                />
              </Group>
            )}
          <TagsInput
            label="Atribúty"
            placeholder="Farba, veľkosť..."
            data={
              data?.product?.variants?.some((variant) => variant.attributes?.length)
                ? Array.from(
                    new Set(
                      data.product.variants.flatMap(
                        (variant) => variant.attributes?.map((attr) => attr.name) ?? []
                      )
                    )
                  )
                : ['farba', 'velkost'] // Fallback to default when no attributes exist
            }
            value={attributes}
            onChange={updateAttributes}
            acceptValueOnBlur
          />
          <Grid>
            {form.getValues().variants.map((variant, index) => (
              <Grid.Col span={{ base: 12, md: 6 }} key={variant.id ?? index}>
                <Fieldset
                  p="sm"
                  legend={`Variant ${index + 1}${
                    form.errors[`variants.${index}.attributes`]
                      ? ` (${form.errors[`variants.${index}.attributes`]})`
                      : ''
                  }${form.values.variants[index].stock === 0 ? ' (Nedostupný)' : ''}`}
                  c={
                    form.errors[`variants.${index}.attributes`]
                      ? 'red'
                      : form.values.variants[index].stock === 0
                        ? 'orange'
                        : undefined
                  }
                >
                  <Group wrap="nowrap">
                    {variant.attributes?.map((attr, attrIndex) => (
                      <TextInput
                        ref={index === 0 && attrIndex === 0 ? inputRef : null}
                        key={attr.id ?? `${index}-${attr.name}`}
                        label={attr.name.charAt(0).toUpperCase() + attr.name.slice(1)}
                        placeholder={attr.name === 'farba' ? 'Červená?...' : '36?...'}
                        c={
                          form.errors[`variants.${index}.attributes`]
                            ? 'red'
                            : attr.name === 'farba' &&
                                !colorMap[
                                  form.values.variants[index].attributes?.[attrIndex]?.value ?? ''
                                ]
                              ? 'orange'
                              : undefined
                        }
                        rightSection={
                          attr.name === 'farba' &&
                          colorMap[
                            form.values.variants[index].attributes?.[attrIndex]?.value ?? ''
                          ] ? (
                            <ColorSwatch
                              size={16}
                              color={
                                colorMap[
                                  form.values.variants[index].attributes?.[attrIndex]?.value ?? ''
                                ]
                              }
                            />
                          ) : undefined
                        }
                        {...form.getInputProps(`variants.${index}.attributes.${attrIndex}.value`)}
                      />
                    ))}
                    <NumberInput
                      hideControls
                      min={0}
                      allowNegative={false}
                      allowDecimal={false}
                      label="Sklad"
                      inputContainer={(children) => (
                        <Group align="center" wrap="nowrap">
                          {children}
                          <ActionIcon
                            color="red"
                            onClick={() => {
                              form.removeListItem('variants', index);
                            }}
                            disabled={form.getValues().variants.length === 1}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      )}
                      onKeyDown={(e) => {
                        if (e.key !== 'Tab' || index !== 0) return;

                        e.preventDefault();

                        addVariant();
                      }}
                      {...form.getInputProps(`variants.${index}.stock`)}
                    />
                  </Group>
                </Fieldset>
              </Grid.Col>
            ))}
          </Grid>
        </Paper>
      </Stack>
    </form>
  );
};
